/**
 * Cloudflare Turnstile, for the pages that must prove a browser is a browser.
 *
 * Shared by both sites: the corpus site passes it once to start a reading session
 * (`POST /api/visitor`), and the root site's sign-up, resend and reset forms pass
 * it per submit. One loader, so the two cannot disagree about how the widget is
 * drawn or what an empty site key means.
 *
 * **An empty site key means no check**, and the server agrees: a deployment
 * without Turnstile keys switches the check off on both sides (`/me` hands out the
 * key, and `verify_turnstile` passes when disabled). So development needs no keys
 * and never loads Cloudflare's script.
 *
 * The widget renders `interaction-only`: invisible for the ordinary visitor, and
 * shown in a small panel only when Cloudflare wants a click.
 */

// standards: const-environment-literal exempt -- Cloudflare's published script address, the same in every
// deployment and every environment: a third party's fixed contract, not a value of ours that
// differs per deployment. The site key, which does differ, comes from /me.
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/** One load per page, shared by every caller. */
let loading = null

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (loading) return loading
  loading = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_URL
    script.async = true
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error('The bot check did not load.'))
    script.onerror = () => {
      loading = null
      reject(new Error('The bot check could not be loaded. Check your connection and try again.'))
    }
    document.head.append(script)
  })
  return loading
}

/**
 * A fresh Turnstile token, or `null` when this deployment runs no check.
 *
 * Each token is good for one verification, so this is called per submit rather
 * than cached. Rejects with a reader-facing message if the check fails or times
 * out — the caller shows it, and trying again draws a new widget.
 */
export async function turnstileToken(siteKey) {
  if (!siteKey) return null
  const api = await loadTurnstile()
  const holder = document.createElement('div')
  holder.className = 'turnstile-holder'
  document.body.append(holder)

  return new Promise((resolve, reject) => {
    let widgetId = null
    const finish = (settle) => (value) => {
      if (widgetId !== null) api.remove(widgetId)
      holder.remove()
      settle(value)
    }
    widgetId = api.render(holder, {
      sitekey: siteKey,
      appearance: 'interaction-only',
      callback: finish(resolve),
      'error-callback': () => finish(reject)(new Error('The bot check did not pass. Reload the page and try again.')),
      'timeout-callback': () => finish(reject)(new Error('The bot check timed out. Please try again.')),
    })
  })
}
